import React, { useState, useEffect } from "react";
import { Button, Card, Col, Row, Form, Spinner } from "react-bootstrap";
import { useAuth } from '../../utils/AuthContext';
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import ReactApexChart from "react-apexcharts";
import { getDataOnRealtimeDb } from '../../utils/firebase';
import moment from 'moment';
import { jsPDF } from "jspdf";
import 'jspdf-autotable';

const filterDataByDate = (data, startDate, endDate) => {
    if (!data) return [];
    return Object.values(data).filter(item => {
        const itemDate = moment(item.date);
        return itemDate.isBetween(startDate, endDate, 'day', '[]');
    });
};

const calculateTotal = (items) => {
    if (!items) return 0;
    return Object.values(items).reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
};

const calculateCategoryBreakdown = (items) => {
    if (!items) return {};
    return Object.values(items).reduce((acc, item) => {
        const category = item.category || 'Other';
        acc[category] = (acc[category] || 0) + parseFloat(item.amount || 0);
        return acc;
    }, {});
};

const calculateTrends = (expenses, incomes, startDate, endDate) => {
    const getDates = () => {
        const dates = [];
        let current = startDate.clone();
        while (current.isSameOrBefore(endDate)) {
            dates.push(current.format('YYYY-MM-DD'));
            current.add(1, 'days');
        }
        return dates;
    };

    const dates = getDates();
    const expensesByDate = {};
    const incomesByDate = {};

    // Initialize all dates with 0
    dates.forEach(date => {
        expensesByDate[date] = 0;
        incomesByDate[date] = 0;
    });

    // Aggregate expenses by date
    Object.values(expenses || {}).forEach(expense => {
        const date = moment(expense.date).format('YYYY-MM-DD');
        if (expensesByDate[date] !== undefined) {
            expensesByDate[date] += parseFloat(expense.amount || 0);
        }
    });

    // Aggregate incomes by date
    Object.values(incomes || {}).forEach(income => {
        const date = moment(income.date).format('YYYY-MM-DD');
        if (incomesByDate[date] !== undefined) {
            incomesByDate[date] += parseFloat(income.amount || 0);
        }
    });

    return {
        series: [
            {
                name: 'Income',
                data: Object.values(incomesByDate)
            },
            {
                name: 'Expenses',
                data: Object.values(expensesByDate)
            }
        ],
        options: {
            chart: {
                type: 'line',
                toolbar: {
                    show: false
                }
            },
            stroke: {
                curve: 'smooth',
                width: 2
            },
            xaxis: {
                categories: dates.map(date => moment(date).format('MMM DD')),
                labels: {
                    rotate: -45,
                    rotateAlways: true
                }
            },
            yaxis: {
                labels: {
                    formatter: (value) => `$${value.toFixed(2)}`
                }
            },
            colors: ['#33d685', '#506fd9'],
            legend: {
                position: 'top'
            },
            grid: {
                borderColor: 'rgba(72,94,144, 0.07)',
                padding: {
                    top: -20
                }
            },
            tooltip: {
                y: {
                    formatter: (value) => `$${value.toFixed(2)}`
                }
            }
        }
    };
};

const calculateCashFlow = (incomes, expenses, bills, startDate, endDate) => {
    const getDates = () => {
        const dates = [];
        let current = startDate.clone();
        while (current.isSameOrBefore(endDate)) {
            dates.push(current.format('YYYY-MM-DD'));
            current.add(1, 'days');
        }
        return dates;
    };

    const dates = getDates();
    const cashFlow = {};
    const netIncome = {};

    // Initialize dates
    dates.forEach(date => {
        cashFlow[date] = {
            inflow: 0,
            outflow: 0
        };
        netIncome[date] = 0;
    });

    // Calculate daily cash flow
    Object.values(incomes || {}).forEach(income => {
        const date = moment(income.date).format('YYYY-MM-DD');
        if (cashFlow[date]) {
            cashFlow[date].inflow += parseFloat(income.amount || 0);
        }
    });

    Object.values(expenses || {}).forEach(expense => {
        const date = moment(expense.date).format('YYYY-MM-DD');
        if (cashFlow[date]) {
            cashFlow[date].outflow += parseFloat(expense.amount || 0);
        }
    });

    Object.values(bills || {}).forEach(bill => {
        const date = moment(bill.dueDate).format('YYYY-MM-DD');
        if (cashFlow[date]) {
            cashFlow[date].outflow += parseFloat(bill.amount || 0);
        }
    });

    // Calculate cumulative net income
    let runningTotal = 0;
    dates.forEach(date => {
        const dailyNet = cashFlow[date].inflow - cashFlow[date].outflow;
        runningTotal += dailyNet;
        netIncome[date] = runningTotal;
    });

    return {
        series: [
            {
                name: 'Cash Inflow',
                data: Object.values(cashFlow).map(day => day.inflow)
            },
            {
                name: 'Cash Outflow',
                data: Object.values(cashFlow).map(day => -day.outflow)
            },
            {
                name: 'Net Income',
                data: Object.values(netIncome)
            }
        ],
        options: {
            chart: {
                type: 'bar',
                stacked: true,
                toolbar: {
                    show: false
                }
            },
            plotOptions: {
                bar: {
                    columnWidth: '60%'
                }
            },
            colors: ['#33d685', '#506fd9', '#ffc107'],
            stroke: {
                width: [0, 0, 3],
                curve: 'smooth'
            },
            xaxis: {
                categories: dates.map(date => moment(date).format('MMM DD')),
                labels: {
                    rotate: -45,
                    rotateAlways: true
                }
            },
            yaxis: {
                labels: {
                    formatter: (value) => `$${Math.abs(value).toFixed(0)}`
                }
            },
            legend: {
                position: 'top'
            },
            fill: {
                opacity: [0.85, 0.85, 1],
                gradient: {
                    inverseColors: false,
                    shade: 'light',
                    type: "vertical",
                    opacityFrom: 0.85,
                    opacityTo: 0.55,
                    stops: [0, 100, 100, 100]
                }
            },
            tooltip: {
                shared: true,
                intersect: false,
                y: {
                    formatter: (value) => `$${Math.abs(value).toFixed(2)}`
                }
            }
        }
    };
};

const generatePDF = (reportData, dateRange) => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text('Financial Report', 20, 20);
    
    // Add date range
    doc.setFontSize(12);
    doc.text(`Period: ${moment(dateRange.start).format('MMM DD, YYYY')} - ${moment(dateRange.end).format('MMM DD, YYYY')}`, 20, 30);

    // Add summary section
    doc.text('Summary', 20, 45);
    const summaryData = [
        ['Total Income', `$${reportData.summary.totalIncome.toFixed(2)}`],
        ['Total Expenses', `$${reportData.summary.totalExpenses.toFixed(2)}`],
        ['Total Bills', `$${reportData.summary.totalBills.toFixed(2)}`],
        ['Net Income', `$${reportData.summary.netIncome.toFixed(2)}`]
    ];
    
    doc.autoTable({
        startY: 50,
        head: [['Category', 'Amount']],
        body: summaryData,
        theme: 'grid'
    });

    // Add expense categories section
    doc.text('Expense Categories', 20, doc.lastAutoTable.finalY + 20);
    const categoryData = Object.entries(reportData.categoryBreakdown.expenses)
        .map(([category, amount]) => [category, `$${amount.toFixed(2)}`]);
    
    doc.autoTable({
        startY: doc.lastAutoTable.finalY + 25,
        head: [['Category', 'Amount']],
        body: categoryData,
        theme: 'striped'
    });

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.text(`Generated on ${moment().format('MMM DD, YYYY, h:mm A')}`, 20, doc.internal.pageSize.height - 10);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 10);
    }

    // Save the PDF
    doc.save(`financial-report-${moment().format('YYYY-MM-DD')}.pdf`);
};

export default function FinancialReport() {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [timeFrame, setTimeFrame] = useState('month');
    const [reportData, setReportData] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: moment().startOf('month').format('YYYY-MM-DD'),
        end: moment().endOf('month').format('YYYY-MM-DD')
    });

    useEffect(() => {
        fetchReportData();
    }, [timeFrame, dateRange]);

    const fetchReportData = async () => {
        setLoading(true);
        try {
            // Fetch all required data
            const expenses = await getDataOnRealtimeDb(`users/${currentUser.uid}/expenses`);
            const incomes = await getDataOnRealtimeDb(`users/${currentUser.uid}/incomes`);
            const budgets = await getDataOnRealtimeDb(`users/${currentUser.uid}/budgets`);
            const bills = await getDataOnRealtimeDb(`users/${currentUser.uid}/bills`);

            // Process and aggregate data
            const processedData = processData(expenses, incomes, budgets, bills);
            setReportData(processedData);
        } catch (error) {
            console.error('Error fetching report data:', error);
        } finally {
            setLoading(false);
        }
    };

    const processData = (expenses, incomes, budgets, bills) => {
        const startDate = moment(dateRange.start);
        const endDate = moment(dateRange.end);

        // Filter data based on date range
        const filteredExpenses = filterDataByDate(expenses, startDate, endDate);
        const filteredIncomes = filterDataByDate(incomes, startDate, endDate);
        const filteredBills = filterDataByDate(bills, startDate, endDate);

        // Calculate summaries
        const summary = {
            totalIncome: calculateTotal(filteredIncomes),
            totalExpenses: calculateTotal(filteredExpenses),
            totalBills: calculateTotal(filteredBills),
            netIncome: calculateTotal(filteredIncomes) - calculateTotal(filteredExpenses) - calculateTotal(filteredBills)
        };

        // Calculate category breakdowns
        const categoryBreakdown = {
            expenses: calculateCategoryBreakdown(filteredExpenses),
            income: calculateCategoryBreakdown(filteredIncomes)
        };

        // Calculate trends
        const trends = calculateTrends(filteredExpenses, filteredIncomes, startDate, endDate);

        // Add cash flow analysis
        const cashFlowData = calculateCashFlow(incomes, expenses, bills, startDate, endDate);

        return {
            summary,
            categoryBreakdown,
            trends,
            cashFlow: cashFlowData
        };
    };

    const handleTimeFrameChange = (newTimeFrame) => {
        const now = moment();
        let start, end;

        switch (newTimeFrame) {
            case 'week':
                start = now.clone().startOf('week');
                end = now.clone().endOf('week');
                break;
            case 'month':
                start = now.clone().startOf('month');
                end = now.clone().endOf('month');
                break;
            case 'year':
                start = now.clone().startOf('year');
                end = now.clone().endOf('year');
                break;
            default:
                return;
        }

        setTimeFrame(newTimeFrame);
        setDateRange({
            start: start.format('YYYY-MM-DD'),
            end: end.format('YYYY-MM-DD')
        });
    };

    const downloadPDF = () => {
        if (!reportData) return;
        generatePDF(reportData, dateRange);
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                        <h4 className="main-title mb-0">Financial Report</h4>
                    </div>
                    <Button variant="primary" onClick={downloadPDF}>
                        <i className="ri-download-2-line me-2"></i>
                        Download Report
                    </Button>
                </div>

                <Card className="card-one mb-4">
                    <Card.Body>
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div>
                                <Form.Group>
                                    <Form.Label>Time Frame</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant={timeFrame === 'week' ? 'primary' : 'outline-primary'}
                                            onClick={() => handleTimeFrameChange('week')}
                                        >
                                            Week
                                        </Button>
                                        <Button 
                                            variant={timeFrame === 'month' ? 'primary' : 'outline-primary'}
                                            onClick={() => handleTimeFrameChange('month')}
                                        >
                                            Month
                                        </Button>
                                        <Button 
                                            variant={timeFrame === 'year' ? 'primary' : 'outline-primary'}
                                            onClick={() => handleTimeFrameChange('year')}
                                        >
                                            Year
                                        </Button>
                                    </div>
                                </Form.Group>
                            </div>
                            <div>
                                <Form.Group>
                                    <Form.Label>Custom Date Range</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="date"
                                            value={dateRange.start}
                                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                        />
                                        <Form.Control
                                            type="date"
                                            value={dateRange.end}
                                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                        />
                                    </div>
                                </Form.Group>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {/* Summary Cards */}
                <Row className="g-3 mb-4">
                    <Col md={3}>
                        <Card className="card-one">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <h6 className="card-title">Total Income</h6>
                                    <i className="ri-money-dollar-circle-line fs-24 text-success"></i>
                                </div>
                                <h3 className="card-value">${reportData.summary.totalIncome.toFixed(2)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="card-one">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <h6 className="card-title">Total Expenses</h6>
                                    <i className="ri-shopping-cart-2-line fs-24 text-danger"></i>
                                </div>
                                <h3 className="card-value">${reportData.summary.totalExpenses.toFixed(2)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="card-one">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <h6 className="card-title">Total Bills</h6>
                                    <i className="ri-bill-line fs-24 text-warning"></i>
                                </div>
                                <h3 className="card-value">${reportData.summary.totalBills.toFixed(2)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="card-one">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <h6 className="card-title">Net Income</h6>
                                    <i className="ri-wallet-3-line fs-24 text-primary"></i>
                                </div>
                                <h3 className="card-value">${reportData.summary.netIncome.toFixed(2)}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Charts Section */}
                <Row className="g-3">
                    <Col md={6}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title>Income vs Expenses</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <ReactApexChart
                                    options={reportData.trends.options}
                                    series={reportData.trends.series}
                                    type="line"
                                    height={350}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title>Expense Categories</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <ReactApexChart
                                    options={{
                                        chart: {
                                            type: 'donut'
                                        },
                                        labels: Object.keys(reportData.categoryBreakdown.expenses),
                                        colors: ['#506fd9', '#85b6ff', '#33d685', '#0dcaf0', '#1c96e9', '#6e7985'],
                                        legend: {
                                            position: 'bottom'
                                        },
                                        plotOptions: {
                                            pie: {
                                                donut: {
                                                    size: '70%'
                                                }
                                            }
                                        }
                                    }}
                                    series={Object.values(reportData.categoryBreakdown.expenses)}
                                    type="donut"
                                    height={350}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Cash Flow Section */}
                <Row className="g-3 mt-4">
                    <Col xs={12}>
                        <Card className="card-one">
                            <Card.Header>
                                <Card.Title>Cash Flow Overview</Card.Title>
                            </Card.Header>
                            <Card.Body>
                                <ReactApexChart
                                    options={reportData.cashFlow.options}
                                    series={reportData.cashFlow.series}
                                    type="line"
                                    height={400}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Financial Metrics */}
                <Row className="g-3 mt-4">
                    <Col md={4}>
                        <Card className="card-one">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <h6 className="card-title">Cash Flow Ratio</h6>
                                    <i className="ri-exchange-funds-line fs-24 text-info"></i>
                                </div>
                                <h3 className="card-value">
                                    {(reportData.summary.totalIncome / Math.max(1, reportData.summary.totalExpenses)).toFixed(2)}
                                </h3>
                                <small className="text-muted">Income to Expense Ratio</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="card-one">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <h6 className="card-title">Savings Rate</h6>
                                    <i className="ri-funds-box-line fs-24 text-success"></i>
                                </div>
                                <h3 className="card-value">
                                    {((reportData.summary.netIncome / reportData.summary.totalIncome) * 100).toFixed(1)}%
                                </h3>
                                <small className="text-muted">Net Income / Total Income</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="card-one">
                            <Card.Body>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <h6 className="card-title">Expense Coverage</h6>
                                    <i className="ri-shield-check-line fs-24 text-warning"></i>
                                </div>
                                <h3 className="card-value">
                                    {(reportData.summary.totalIncome / (reportData.summary.totalExpenses / 30)).toFixed(1)} days
                                </h3>
                                <small className="text-muted">Days of Expense Coverage</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Footer />
            </div>
        </React.Fragment>
    );
} 